import { itx } from "../../../src/translationItx/itx";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { C1_PRODUCT_NAME } from "../C1_Const";
import "@fontsource/montserrat/300.css";
import { isConceptOneHost } from "../../../src/conceptOne/C1_host";
import { C1_UserAvatar } from "./C1_UserAvatar";
import { enumAvailableLanguages } from "../../../src/models/SettingsEnums";

type AppHeaderProps = {
  title: ReactNode;
  children?: ReactNode;
  center?: boolean;
  big?: boolean;
  onProductClick?: () => void;
  logoutTarget?: string;
};

const languageOptions = [
  { language: enumAvailableLanguages.german, label: "Deutsch" },
  { language: enumAvailableLanguages.english, label: "English" },
  { language: enumAvailableLanguages.spanish, label: "Español" },
  { language: enumAvailableLanguages.french, label: "Français" },
  { language: enumAvailableLanguages.italian, label: "Italiano" },
];

function C1_LanguageFlag({ language }: { language: enumAvailableLanguages }) {
  if (language === enumAvailableLanguages.spanish) {
    return (
      <svg className="C1_languageFlag" viewBox="0 0 30 20" aria-hidden="true">
        <path fill="#aa151b" d="M0 0h30v20H0z" />
        <path fill="#f1bf00" d="M0 5h30v10H0z" />
      </svg>
    );
  }

  if (language === enumAvailableLanguages.french || language === enumAvailableLanguages.italian) {
    return (
      <svg className="C1_languageFlag" viewBox="0 0 30 20" aria-hidden="true">
        <path fill={language === enumAvailableLanguages.french ? "#002395" : "#009246"} d="M0 0h10v20H0z" />
        <path fill="#fff" d="M10 0h10v20H10z" />
        <path fill={language === enumAvailableLanguages.french ? "#ed2939" : "#ce2b37"} d="M20 0h10v20H20z" />
      </svg>
    );
  }

  if (language === enumAvailableLanguages.german) {
    return (
      <svg className="C1_languageFlag" viewBox="0 0 30 20" aria-hidden="true">
        <path fill="#000" d="M0 0h30v6.67H0z" />
        <path fill="#d00" d="M0 6.67h30v6.66H0z" />
        <path fill="#ffce00" d="M0 13.33h30V20H0z" />
      </svg>
    );
  }

  return (
    <svg className="C1_languageFlag" viewBox="0 0 60 40" aria-hidden="true">
      <path fill="#012169" d="M0 0h60v40H0z" />
      <path stroke="#fff" strokeWidth="8" d="m0 0 60 40M60 0 0 40" />
      <path stroke="#c8102e" strokeWidth="4" d="m0 0 60 40M60 0 0 40" />
      <path stroke="#fff" strokeWidth="13" d="M30 0v40M0 20h60" />
      <path stroke="#c8102e" strokeWidth="7" d="M30 0v40M0 20h60" />
    </svg>
  );
}

export function C1_AppHeader({ title, children, center = false, big = false, onProductClick, logoutTarget }: AppHeaderProps) {
  useOnChangeWatcher(state.settings);
  const [languageOptionsOpen, setLanguageOptionsOpen] = useState(false);
  const languageComboboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!languageOptionsOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!languageComboboxRef.current?.contains(event.target as Node)) {
        setLanguageOptionsOpen(false);
      }
    };

    window.addEventListener("pointerdown", closeOnOutsideClick);
    return () => window.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [languageOptionsOpen]);

  const selectLanguage = (language: enumAvailableLanguages) => {
    state.settings.uiLanguage = language;
    setLanguageOptionsOpen(false);
  };

  return (
    <header className={`C1_appHeader${center ? " C1_appHeader--center" : ""}${big ? " big" : ""}`}>
      {onProductClick ? (
        <button
          type="button"
          className="C1_brandButton"
          title={itx("c1.ui.Zur_ESTADA_Projektauswahl", "Zur ESTADA-Projektauswahl")}
          aria-label={itx("c1.ui.Zur_ESTADA_Projektauswahl", "Zur ESTADA-Projektauswahl")}
          onClick={onProductClick}
        >
          <img className="C1_brandImage" src={`${import.meta.env.BASE_URL}images/logo.png`} alt="" aria-hidden="true" />
        </button>
      ) : (
        <img className="C1_brandImage" src={`${import.meta.env.BASE_URL}images/logo.png`} alt={`ESTADA ${C1_PRODUCT_NAME} Logo`} />
      )}
      <div className="C1_brandName">ESTADA</div>
      <div className="C1_productName">{title}</div>
      <div className="C1_appHeaderActions">
        {children}
        <div className="C1_languageCombobox" ref={languageComboboxRef}>
          <button
            type="button"
            className="C1_languageSelect"
            role="combobox"
            aria-label={itx("ServerTemplateList.selectLanguage", "Sprache auswählen")}
            aria-expanded={languageOptionsOpen}
            aria-controls="C1_languageOptions"
            onClick={() => setLanguageOptionsOpen((open) => !open)}
          >
            <C1_LanguageFlag language={state.settings.language} />
            <span aria-hidden="true">▾</span>
          </button>
          {languageOptionsOpen && (
            <div id="C1_languageOptions" className="C1_languageOptions" role="listbox">
              {languageOptions.map(({ language, label }) => (
                <button
                  key={language}
                  type="button"
                  role="option"
                  aria-label={label}
                  title={label}
                  aria-selected={state.settings.language === language}
                  onClick={() => selectLanguage(language)}
                >
                  <C1_LanguageFlag language={language} />
                </button>
              ))}
            </div>
          )}
        </div>
        <C1_UserAvatar logoutTarget={logoutTarget ?? (isConceptOneHost() ? "/" : "/c1")} />
      </div>
    </header>
  );
}
